import React, { FC, useMemo } from 'react';
import { Category, DemandArea } from '../../../../lib/types/endUser';
import { CoreVisualSectionT } from '../../../../lib/types/sections';
import { ApplyCampaignBadgeT } from '../../../../lib/hooks/endUserHooks/useApplyCampaignHouseBadge';
import { useAppDispatch, useAppSelector } from '../../../../lib/store/hooks';
import { END_USER_FRENCH_TENANT_MODEL_NAME } from '../../../../lib/utils/constants';
import { useIsEnergieScwabenTenant } from '../../../../lib/hooks/endUserHooks/useIsEnergieScwabenTenant';
import { useNavigate } from 'react-router-dom';
import { sendDataToAnalytics } from '../../../../lib/utils/sendDataToAnalytics';
import { sortCategoriesByPriority } from '../../../../lib/utils/helpers';
import { checkBoughtProductsInCategory } from '../../../../lib/utils/end-user-helpers';
import { setCurrentCategory } from '../../../../lib/store/slices/endUserSlice';
import { ROUTER_PATHS } from '../../../../lib/utils/router-paths';
import { useGetCampaignObjectPositions } from '../../../../lib/hooks/endUserHooks/useGetCampaignObjectPositions';
import {
  CAMPAIGN_POSITION_ELEMENT_OPTIONS,
  CAMPAIGN_POSITION_PAGES_OPTIONS,
  CAMPAIGN_POSITION_SECTION_OPTIONS,
} from '../../../../modules/campaignDetails/hooks/useGetPositionOption';
import { Button } from 'antd';
import styles from '../../../../modules/endUserHouseModel/components/EndUserHouseModelMobile/index.module.scss';
import { EndUserTypography } from '../../../EndUserTypography';
import { CampaignObjectImage } from '../../../CampaignObjectImage';
import { FinderButton } from '../../../FinderButton';
import { EviNewHouseModelMobile } from '../EviNewHouseModelMobile';

type Props = {
  demandAreas: DemandArea[];
  content?: CoreVisualSectionT;
  applyCampaignHouseBadge: ApplyCampaignBadgeT;
};

export const EviNewHouseModelMobileWrapper: FC<Props> = ({
  demandAreas,
  content,
  applyCampaignHouseBadge,
}) => {
  const { demandArea } = useAppSelector(({ endUser }) => endUser);
  const dispatch = useAppDispatch();
  const isFrenchTenant = demandArea?.project?.name === END_USER_FRENCH_TENANT_MODEL_NAME;
  const { isEnergieSchwaben } = useIsEnergieScwabenTenant();

  const navigate = useNavigate();
  const handleAreaClick = (areaId: number, name: string) => {
    sendDataToAnalytics(
      'Bedarfsbereich',
      `Aufruf Bedarfsbereich ${name}`,
      `User exploriert Produkte aus Bedarfsbereich ${name}`,
    );

    const demandArea = demandAreas.find((area) => area.id === areaId);

    const sortedCategories = sortCategoriesByPriority(demandArea?.categories || []);

    let category: Category | undefined = sortedCategories[0];

    //hardcode below
    if (isEnergieSchwaben) {
      if (demandArea?.name === 'Energie') {
        const gasCategory = sortedCategories.find((category) => category.name === 'Gas');
        const stromCategory = sortedCategories.find(
          (category) => category.name === 'Strom',
        );

        const isGasCovered = gasCategory && checkBoughtProductsInCategory(gasCategory);
        const isStromCovered =
          stromCategory && checkBoughtProductsInCategory(stromCategory);

        if (isGasCovered && !isStromCovered) {
          category = stromCategory;
        } else if (isStromCovered && !isGasCovered) {
          category = gasCategory;
        } else if (isGasCovered && isStromCovered) {
          category = gasCategory;
        } else {
          category = gasCategory;
        }
      }
    }

    setTimeout(() => {
      dispatch(setCurrentCategory({ category }));
    }, 100);

    const path = isFrenchTenant ? ROUTER_PATHS.houseModelFr : ROUTER_PATHS.houseModel;

    navigate(`/${path}/${areaId}`);
  };

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLDivElement>,
    areaId: number,
    name: string,
  ) => {
    if (event.key === 'Enter' || event.key === ' ') {
      handleAreaClick(areaId, name);
    }
  };

  const { foundObjects } = useGetCampaignObjectPositions({
    page: CAMPAIGN_POSITION_PAGES_OPTIONS.LANDING_PAGE,
    sections: [CAMPAIGN_POSITION_SECTION_OPTIONS.HOUSE],
    elements: [CAMPAIGN_POSITION_ELEMENT_OPTIONS.HEADLINE],
  });

  const headlineCampaignObject = useMemo(() => {
    if (foundObjects.length > 0) {
      return foundObjects[0];
    }
  }, [foundObjects]);

  const renderBadge = useMemo(() => {
    if (applyCampaignHouseBadge.isBadgeEnabled) {
      return (
        <Button
          onClick={applyCampaignHouseBadge.onScrollToTeaser}
          className={styles.campaignBadge}
        >
          <img alt="badge" src={applyCampaignHouseBadge.badgeImageUrl} />
        </Button>
      );
    }
  }, [
    applyCampaignHouseBadge.badgeImageUrl,
    applyCampaignHouseBadge.isBadgeEnabled,
    applyCampaignHouseBadge.onScrollToTeaser,
  ]);

  return (
    <div
      style={{
        backgroundImage: `linear-gradient(78.15deg, rgba(0, 0, 0, 0.51) 5.1%, rgba(0, 0, 0, 0.46) 34.64%, rgba(0, 0, 0, 0) 117.95%), url(${content?.backgroundImage?.s3Url})`,
        padding: '24px 16px',
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
      }}
    >
      <div className={styles.headlineBlock}>
        <EndUserTypography
          type="headlineSecond"
          component="Title"
          style={{ marginBottom: '8px', color: '#fff', maxWidth: '14ch' }}
        >
          {content?.headline1}

          {headlineCampaignObject && (
            <CampaignObjectImage
              containerClassName={styles.campaignElement}
              campaignObject={headlineCampaignObject}
            />
          )}
        </EndUserTypography>
        <EndUserTypography
          type="headlineSixth"
          component="Title"
          style={{ marginBottom: 12, color: '#fff' }}
        >
          {content?.headline2}
        </EndUserTypography>

        <FinderButton
          showFinderButton={content?.finderEnabled}
          finderButtonText={content?.finderButtonText}
          finderButtonUrl={content?.finderButtonLink}
          recommendForAllCategories={true}
        />
        {renderBadge}
      </div>
      <EviNewHouseModelMobile
        demandAreas={demandAreas}
        handleAreaClick={handleAreaClick}
        handleKeyDown={handleKeyDown}
      />
    </div>
  );
};
